import React, { useEffect } from "react";
import "./FollowUpChips.css";

/**
 * FollowUpChips — clickable tap-to-reply chips rendered above the universal
 * command bar whenever the brain returns `suggested_replies`. Lets the owner
 * answer the AI's last question without picking up the mic or typing.
 *
 * Auto-fade: handled entirely in CSS via the `chips-show-and-fade`
 * keyframes animation — 0.3s rise, 3.4s hold, 0.6s fade. On animation end
 * we call onDismiss. Hover pauses the CSS animation so the chips stay put
 * while the owner reads them. No state, no timers in JS — keeps the React
 * Compiler happy.
 *
 * Keyboard shortcuts: press 1-4 to pick chip 1-4. Esc to dismiss.
 */
export default function FollowUpChips({ options, onPick, onDismiss }) {
  const optionsKey = Array.isArray(options) ? options.join("|") : "";

  useEffect(() => {
    if (!optionsKey) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Escape") { e.preventDefault(); onDismiss?.(); return; }
      const n = parseInt(e.key, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= options.length) {
        e.preventDefault();
        onPick?.(options[n - 1]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [optionsKey, options, onPick, onDismiss]);

  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <div
      key={optionsKey /* force re-mount on new options so the CSS animation restarts */}
      className="follow-up-chips"
      data-testid="follow-up-chips"
      role="group"
      aria-label="Quick reply options"
      onAnimationEnd={(e) => {
        // Only the outer keyframes animation fires — child hover transitions
        // are CSS transitions, not animations, so they don't trigger this.
        if (e.animationName === "chips-show-and-fade") onDismiss?.();
      }}
    >
      <button
        type="button"
        className="follow-up-dismiss"
        onClick={onDismiss}
        data-testid="follow-up-dismiss"
        aria-label="Dismiss reply options"
        title="Dismiss (Esc)"
      >
        ×
      </button>
      {options.map((opt, i) => (
        <button
          key={`${i}-${opt}`}
          type="button"
          className="follow-up-chip"
          onClick={() => onPick?.(opt)}
          data-testid={`follow-up-chip-${i}`}
          title={`Press ${i + 1} or click`}
        >
          <span className="follow-up-chip-key">{i + 1}</span>
          <span className="follow-up-chip-label">{opt}</span>
        </button>
      ))}
    </div>
  );
}
